import { API_URL, authHeaders, resolvePath } from '../config/api';

// ---- Brand onboarding without Shopify + manual products ----
//
// Contract: outfino-web-contracts/portal-onboarding-drops.md (Outfino/api
// feature/brand-onboarding-products).
//
//   POST /brands                          multipart: name, logo, cover,
//                                         websiteDomain, termsAccepted, termsVersion
//     201 { code, message: { brand, membership, embed }, details }
//   POST /brands/:brandId/products/manual multipart: images (1–8), name, price,
//                                         currency, sizes (repeated), description
//                                         (editProducts; no webshop link since drops v2)
//     201 { code, message: { product, labels, isClothing, usable, reason }, details }
//
// Errors on both: { success: false, error: '<CODE>', field?, index? }, with a
// Retry-After header on 429 RATE_LIMITED. A JSON POST /brands is the old
// admin-only Shopify create, so these always go out as multipart.

async function sendForm(path, form) {
  const res = await fetch(`${API_URL}${resolvePath(path, 'POST')}`, {
    method: 'POST',
    // No Content-Type: the browser sets the multipart boundary itself.
    headers: await authHeaders(),
    body: form,
  });
  let data = null;
  try {
    data = await res.json();
  } catch {
    // no / invalid JSON body
  }
  if (!res.ok) {
    const err = new Error((typeof data?.error === 'string' && data.error) || `HTTP ${res.status}`);
    err.status = res.status;
    err.data = data;
    // Readable only when the API exposes it over CORS; the page falls back to
    // a plain "try again later" without it.
    const retry = Number(res.headers?.get?.('Retry-After'));
    if (Number.isFinite(retry) && retry > 0) err.retryAfter = retry;
    throw err;
  }
  return data && typeof data === 'object' && 'message' in data ? data.message : data;
}

/// The error a write answered with, whichever envelope it came in:
///   new endpoints      { success: false, error, field?, index? }
///   drop endpoints     { message: { success: false, error } }
///   permission layer   { success: false, error, message }
/// → { status, code, field, index, retryAfter }.
export function errorInfo(err) {
  const d = err?.data;
  const inner = d && typeof d.message === 'object' && d.message ? d.message : null;
  const code =
    (typeof d?.error === 'string' && d.error)
    || (inner && typeof inner.error === 'string' && inner.error)
    || (typeof d?.message === 'string' && d.message)
    || null;
  return {
    status: err?.status ?? null,
    code,
    field: d?.field ?? inner?.field ?? null,
    index: Number.isInteger(d?.index) ? d.index : Number.isInteger(inner?.index) ? inner.index : null,
    retryAfter: err?.retryAfter ?? null,
  };
}

export function buildBrandForm({ name, logo, cover, websiteDomain, termsVersion }) {
  const form = new FormData();
  form.append('name', name);
  form.append('websiteDomain', websiteDomain);
  form.append('termsAccepted', 'true');
  form.append('termsVersion', termsVersion);
  if (logo) form.append('logo', logo);
  if (cover) form.append('cover', cover);
  return form;
}

/// → { brand, membership, embed }.
export function createBrand(fields) {
  return sendForm('/brands', buildBrandForm(fields));
}

export function buildManualProductForm({ images, name, price, currency, sizes, description }) {
  const form = new FormData();
  (images || []).forEach((file) => form.append('images', file));
  form.append('name', name);
  form.append('price', price);
  form.append('currency', currency);
  (sizes || []).forEach((size) => form.append('sizes', size));
  if (description) form.append('description', description);
  // No `publish`: a drop takes DRAFT / UNLISTED products only.
  return form;
}

/// → { product, labels, isClothing, usable, reason }.
export function uploadManualProduct(brandId, fields) {
  return sendForm(`/brands/${brandId}/products/manual`, buildManualProductForm(fields));
}

/// Does the brand have a Shopify store? A brand made in the portal has none:
/// GET /brands/:id says `has_shopify: false`, the session's store says
/// shopifyConnection.status 'NOT_CONNECTED'. Anything else counts as a
/// Shopify brand, as every brand did before.
export function brandHasShopify(brand) {
  if (!brand) return false;
  if (typeof brand.hasShopify === 'boolean') return brand.hasShopify;
  if (typeof brand.has_shopify === 'boolean') return brand.has_shopify;
  const status = brand.shopifyConnection?.status ?? brand.shopify_connection?.status;
  return status !== 'NOT_CONNECTED';
}

/// The new brand in the session's stores[] shape, from the 201 answer: shown
/// at once, and used until the refreshed session lists it (with the caller's
/// owner membership, which the session's members list may not carry).
export function sessionBrandFrom(result, userId) {
  const b = result?.brand;
  if (!b?.id) return null;
  const m = result.membership || { role: 'owner' };
  return {
    id: b.id,
    name: b.store_name || b.name || '',
    companyName: b.store_name || b.name || '',
    logo: b.logo || null,
    coverImage: b.cover_image || null,
    tagline: b.tagline || null,
    description: b.description || '',
    website: b.store_domain ?? null,
    isActive: b.is_active !== false,
    environment: b.environment || null,
    hasShopify: b.has_shopify === true,
    shopifyConnection: b.shopify_connection || { status: 'NOT_CONNECTED' },
    members: [{ userId, role: m.role || 'owner', permissions: m.permissions || {} }],
    shippingCountries: [],
  };
}

/// The session's brands with the just-created one merged in: appended until
/// the session has listed it once (`listed` — after that a missing brand was
/// deleted), and its membership filled in when the session's entry has none.
export function withCreatedBrand(brands, created, { listed = false } = {}) {
  if (!created) return brands;
  const i = brands.findIndex((b) => b.id === created.id);
  if (i === -1) return listed ? brands : [created, ...brands];
  const b = brands[i];
  if (Array.isArray(b.members) && b.members.length) return brands;
  const next = brands.slice();
  next[i] = { ...b, members: created.members, hasShopify: b.hasShopify ?? created.hasShopify };
  return next;
}
